import { useEffect, useRef } from "react";
import type { SpriteIndex } from "../../libs/sprite";

/**
 * One icon cropped out of a sprite PNG, fitted in a `size`px square.
 *
 * Drawn on a canvas at the screen's device pixel ratio with high-quality
 * resampling — a CSS `background` scaled to a fractional size goes soft,
 * especially when shrinking a 2× sprite. Never enlarged past its own size.
 */
export function SpriteIcon({ index, name, size }: { index: SpriteIndex; name: string; size: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const entry = index.atlas[name];
  const pr = entry?.pixelRatio || 1;
  const w = entry ? entry.width / pr : size;
  const h = entry ? entry.height / pr : size;
  const scale = Math.min(1, size / w, size / h);
  const cssW = Math.round(w * scale);
  const cssH = Math.round(h * scale);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !entry) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(index.image, entry.x, entry.y, entry.width, entry.height, 0, 0, canvas.width, canvas.height);
  }, [index, entry, cssW, cssH]);

  if (!entry) return <span style={{ width: size, height: size }} />;
  return (
    <canvas
      ref={ref}
      title={name}
      aria-hidden="true"
      className="inline-block shrink-0"
      style={{ width: cssW, height: cssH }}
    />
  );
}
